import { ChevronsRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';
import { cn } from '@/lib/utils';

const seliseLogo = '/assets/images/selise-hrm-logo.svg';
const seliseSmallLogo = '/assets/images/selise_logo_small.svg';

interface LogoSectionProps {
  open: boolean;
  isMobile: boolean;
  onClose: () => void;
  /** When the rail is collapsed, clicking the mark expands the sidebar. */
  onExpand?: () => void;
}

export const LogoSection = ({ open, isMobile, onClose, onExpand }: Readonly<LogoSectionProps>) => {
  const { t } = useTranslation();
  const showFullLogo = open || isMobile;
  const expandOnMarkClick = Boolean(onExpand) && !showFullLogo;

  return (
    <div
      className={cn(
        'nav-brand flex h-full min-w-0 flex-1 items-center',
        showFullLogo ? 'justify-start px-[14px]' : 'justify-center px-0',
        'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0'
      )}
    >
      <img
        src={seliseLogo}
        alt="SELISE HRM"
        className={cn(
          'h-8 w-auto max-w-[9rem] object-contain object-left',
          !showFullLogo && 'hidden',
          'group-data-[collapsible=icon]:hidden'
        )}
      />
      {expandOnMarkClick ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* shrink-0 keeps the 36px square (same as rail nav items) from being squeezed by flex. */}
            <button
              type="button"
              className={cn(
                'group/expand relative flex size-9 shrink-0 items-center justify-center rounded-md',
                'transition-colors duration-200 hover:bg-muted',
                'outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1'
              )}
              aria-label={t('EXPAND_NAVIGATION')}
              aria-expanded={false}
              aria-controls="app-sidebar"
              onClick={onExpand}
            >
              <img
                src={seliseSmallLogo}
                alt=""
                className="size-6 object-contain transition-opacity duration-150 group-hover/expand:opacity-0 group-focus-visible/expand:opacity-0"
              />
              <ChevronsRight
                strokeWidth={2.5}
                className="absolute size-4 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover/expand:opacity-100 group-focus-visible/expand:opacity-100"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" align="center" sideOffset={8}>
            {t('EXPAND_NAVIGATION')}
          </TooltipContent>
        </Tooltip>
      ) : (
        <img
          src={seliseSmallLogo}
          alt="SELISE"
          className={cn(
            'h-5 w-5 object-contain',
            showFullLogo && 'hidden',
            'group-data-[collapsible=icon]:block'
          )}
        />
      )}

      {isMobile && (
        <button className="ml-auto" onClick={onClose} aria-label="Close sidebar">
          <X size={20} />
        </button>
      )}
    </div>
  );
};
